import { useEffect, useMemo, useState, useRef } from "react";
import * as Sentry from "@sentry/react";
import {
  LiveAvatarSession,
  SessionEvent,
  SessionDisconnectReason,
  AgentEventsEnum,
  VoiceChatEvent,
  ConnectionQuality,
  Language,
} from "@heygen/liveavatar-web-sdk";
import { BotConstants, SessionMode } from "../../libs/constants/index.js";
import BotClient1 from "../../services/api/BotClient.js";
import TenantMngtClient from "../../services/api/TenantMngtClient.js";
import useAppStore from "../../store/AppStore.js";
import useBlendShapeStore from "../../store/ContextStoreBlendshapes.js";
import useLiveAvatarStore from "../../store/LiveAvatarStore.js";
import { useBoundStore } from "../../store/UseBoundStore1.js";
import { useDeepgramAvatarIntegration } from "../../services/deepgram/DeepgramSTT.js";
import DeepgramConfig from "../../config/deepgram.js";

let currentPlaybackMsg = null;
let isAvatarSpeaking = false;
let interrupt = false;
let clearCaption = false;
let startTimeStamp = null;

export default function LoadLiveAvatar() {
  const botClient = useMemo(() => new BotClient1(), []);
  const tenantMngtClient = useMemo(() => new TenantMngtClient(), []);

  const {
    displayBotVisualizer,
    setDisplayBotVisualizer,
    socket,
    setAvatarVideoStream,
    setBotResponse,
    setUserResponse,
    interviewEnded,
    setInterviewEnded,
    addMessage,
    setCanEndServerSessionForLiveAvatar,
    screenSize,
    modelCaptionsController,
    modelCaptionsStream,
    modelCaptionsReader,
    setModelCaptionsStream,
    setModelCaptionsController,
    setModelCaptionsReader,
  } = useAppStore();

  const {
    avatar,
    setAvatar,
    isAvatarSpeakingRef,
    setIsAvatarSpeaking,
    interruptRef,
    setInterrupt,
    clearCaptionRef,
    setClearCaption,
    currentPlaybackMsgRef,
    setCurrentPlaybackMsg,
    avatarMediaStream,
    setAvatarMediaStream,
    avatarEventsController,
    setAvatarEventsStream,
    setAvatarEventsController,
    avatarEventsReader,
    setAvatarEventsReader,
    sysPrompt,
    isAvatarStreamAvailable,
    setIsAvatarStreamAvailable,
    avatarSession,
    setAvatarSession,
    setLiveAvatarSessionId,
    setSessionError,
  } = useLiveAvatarStore();

  const { isSynthesizing, setIsSynthesizing } = useBlendShapeStore();
  const meetingId = useBoundStore((state) => state.meetingId);
  const mode = useBoundStore((state) => state.mode);
  const videoAvatarId = useBoundStore((state) => state.videoAvatarId);
  const videoVoiceId = useBoundStore((state) => state.videoVoiceId);
  const candidateName = useBoundStore((state) => state.username);

  // Deepgram real-time avatar speech recognition
  // This provides real-time transcription independent of AVATAR_TRANSCRIPTION event
  // The real-time transcript is available via setBotResponse for immediate UI updates
  const {
    isConnected: deepgramAvatarConnected,
    isListening: deepgramAvatarListening,
    error: deepgramAvatarError,
    realtimeTranscript: deepgramAvatarTranscript,
    startListening: startDeepgramAvatar,
    stopListening: stopDeepgramAvatar,
    resetTranscripts: resetDeepgramAvatar,
  } = useDeepgramAvatarIntegration({
    useTokenAuth: true,
    model: DeepgramConfig.MODEL,
    language: DeepgramConfig.LANGUAGE,
    smartFormat: DeepgramConfig.SMART_FORMAT,
    interimResults: DeepgramConfig.INTERIM_RESULTS,
    endpointing: DeepgramConfig.ENDPOINTING,
    vadEvents: DeepgramConfig.VAD_EVENTS,
    maxReconnectAttempts: DeepgramConfig.MAX_RECONNECT_ATTEMPTS,
    avatarMediaStream: avatarMediaStream, // Use avatar media stream for audio
    // Disabled: AVATAR_TRANSCRIPTION is now the single source of bot captions.
    // With this false the hook never calls startListening(), so it opens no
    // WebSocket and creates no MediaRecorder. See DeepgramConfig.AVATAR_STT_ENABLED.
    autoStart: DeepgramConfig.AVATAR_STT_ENABLED,
    debugLogging: DeepgramConfig.DEBUG_LOGGING,
    onRealtimeTranscript: (transcript, isFinal) => {
      if (!DeepgramConfig.AVATAR_STT_ENABLED) return;
      if (DeepgramConfig.DEBUG_LOGGING) {
        console.log(
          "[Deepgram Avatar] Real-time transcript:",
          transcript,
          "Final:",
          isFinal,
        );
      }
      // Update bot response with real-time transcript for immediate UI feedback
      // This provides real-time captions while avatar is speaking
      // The AVATAR_TRANSCRIPTION event will still handle the authoritative final text
      if (transcript) {
        // modelCaptionsController?.enqueue(transcript);
        setBotResponse(transcript);
      }
    },
  });

  // Debug logging for Deepgram avatar STT
  useEffect(() => {
    if (DeepgramConfig.DEBUG_LOGGING) {
      console.log("[Deepgram Avatar] Status:", {
        connected: deepgramAvatarConnected,
        listening: deepgramAvatarListening,
        error: deepgramAvatarError,
        transcript: deepgramAvatarTranscript,
        hasAvatarMediaStream: !!avatarMediaStream,
      });
    }
  }, [
    deepgramAvatarConnected,
    deepgramAvatarListening,
    deepgramAvatarError,
    deepgramAvatarTranscript,
    avatarMediaStream,
  ]);

  useEffect(() => {
    isAvatarSpeaking = isAvatarSpeakingRef;
    interrupt = interruptRef;
    clearCaption = clearCaptionRef;
    currentPlaybackMsg = currentPlaybackMsgRef;
  }, [
    isAvatarSpeakingRef,
    interruptRef,
    clearCaptionRef,
    currentPlaybackMsgRef,
  ]);
  useEffect(() => {
    const ref = useRef;
  }, []);

  useEffect(() => {
    if (avatarEventsController && modelCaptionsController) {
      startSession();
    }
  }, [avatarEventsController, modelCaptionsController]);

  useEffect(() => {
    if (interviewEnded) {
      endSession();
      // Stop Deepgram avatar STT when interview ends
      stopDeepgramAvatar();
    }
  }, [interviewEnded]);

  useEffect(() => {
    if (mode === SessionMode.WEB_VIDEO) {
      if (!avatarEventsController) {
        let stream = new ReadableStream({
          start(controller) {
            setAvatarEventsController(controller);
          },
        });
        setAvatarEventsStream(stream);
        let reader = stream.getReader();
        setAvatarEventsReader(reader);
      }

      if (!modelCaptionsController) {
        console.log("Registering ModelCaptionsController...");
        let stream = new ReadableStream({
          start(controller) {
            setModelCaptionsController(controller);
          },
        });
        setModelCaptionsStream(stream);
        let reader = stream.getReader();
        setModelCaptionsReader(reader);
      }
      return () => {
        endSession();
        modelCaptionsController?.close();
        setModelCaptionsController(null);
        avatarEventsController?.close();
        setAvatarEventsController(null);
      };
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (avatarEventsReader && socket) {
        while (true) {
          let { done, value } = await avatarEventsReader.read();
          if (done) {
            console.log("Stream Complete");
            break;
          }
          if (value === "avatar.speak_started") {
            socket.emit("bot-speech-start");
          } else if (value === "avatar.speak_ended") {
            if (currentPlaybackMsg) {
              socket.emit("bot-speech-end", {
                bot_response: currentPlaybackMsg,
              });
              setCurrentPlaybackMsg(null);
            }
          }
        }
      }
    })();
  }, [avatarEventsReader, socket]);

  function getResponseWithoutBreakingWord(text) {
    console.log("liveavatar : getResponseWithoutBreakingWord : text : ", text);
    let charactersToDisplay =
      screenSize === "sm"
        ? -120
        : screenSize === "md"
          ? -150
          : screenSize === "lg"
            ? -180
            : screenSize === "xl"
              ? -220
              : -240;
    let trimmedText = text.slice(charactersToDisplay);

    console.log(
      "liveavatar : getResponseWithoutBreakingWord : trimmedText : initial : ",
      trimmedText,
    );

    const firstSpaceIndex = trimmedText.indexOf(" ");
    trimmedText =
      firstSpaceIndex !== -1
        ? trimmedText.slice(firstSpaceIndex + 1)
        : trimmedText;

    console.log(
      "liveavatar : getResponseWithoutBreakingWord : trimmedText : ",
      trimmedText,
    );
    if (trimmedText?.length > text?.length + 10) {
      trimmedText = ".." + trimmedText;
    }

    return trimmedText;
  }

  // useEffect(() => {
  //   if (isAvatarStreamAvailable && !avatarMediaStream) {
  //     console.log("Stream is Ready...");
  //     console.log("videoRef.current : ", videoRef.current);
  //     const waitForStream = async () => {
  //       while (!videoRef.current?.srcObject) {
  //         // Wait for video element to have a source
  //         await new Promise((resolve) => setTimeout(resolve, 250));
  //       }
  //       console.log("Setting avatarMediaStream : ", videoRef.current.srcObject);
  //       setAvatarMediaStream(videoRef.current.srcObject);
  //     };
  //     waitForStream();
  //   }
  // }, [isAvatarStreamAvailable, avatarMediaStream,videoRef]);

  async function endSession() {
    if (avatarSession) {
      await avatarSession.stop();
      setAvatarSession(null);
    }
    setAvatarMediaStream(null);
  }

  async function startSession() {
    try {
      setSessionError(null);

      // Get LiveAvatar API token from backend
      const authData = await botClient.getLiveAvatarApiToken();
      console.log("LiveAvatar SessionData : ", authData);

      if (authData?.session_id) {
        setLiveAvatarSessionId(authData.session_id);
        Sentry.setTag("liveavatar_session_id", authData.session_id);
      }

      // Create LiveAvatarSession instance with correct constructor
      const session = new LiveAvatarSession(authData.session_token, {
        voiceChat: true,
      });

      setAvatarSession(session);
      setAvatar(session);

      let avatarMessage = "";
      let userMessage = "";
      let shouldEndOnAvatarStop = false;
      let timeout = null;
      let sessionEnded = false;
      let AvatarStartedConversation = false;

      async function recordUserMessage() {
        let uM = userMessage;
        userMessage = "";
        if (uM?.length > 0 && AvatarStartedConversation) {
          addMessage(candidateName, uM, false);
          await tenantMngtClient.recordInteraction({
            meetingId,
            interactionType: "intro",
            currentQuestion: "Work Experience and Background",
            message: uM,
            interactor: "candidate",
            startMarkerOffset: (Date.now() - startTimeStamp) / 1000,
          });
        }
      }

      async function recordAvatarMessage() {
        let aM = avatarMessage;
        avatarMessage = "";
        modelCaptionsController?.enqueue(BotConstants.CLEAR_BOT_CAPTIONS_STR);
        if (aM?.length > 0) {
          addMessage(BotConstants.BOT_NAME, aM, true);
          await tenantMngtClient.recordInteraction({
            meetingId,
            interactionType: "intro",
            currentQuestion: "Work Experience and Background",
            message: aM,
            interactor: "bot",
            startMarkerOffset: (Date.now() - startTimeStamp) / 1000,
          });
        }
      }

      // Shared end-of-interview teardown, used by both the legacy "goodbye" text
      // match and the explicit SESSION_STOPPED / server-initiated disconnect
      // signals, so the recording + flag dance only ever runs once per session.
      function endInterviewAndRecord() {
        if (timeout) clearTimeout(timeout);
        if (!sessionEnded) {
          sessionEnded = true;
          setCanEndServerSessionForLiveAvatar(false);
          Promise.all([recordUserMessage(), recordAvatarMessage()]).finally(
            () => {
              setCanEndServerSessionForLiveAvatar(true);
            },
          );
        }
        timeout = setTimeout(() => {
          setInterviewEnded(true);
        }, 1000);
      }

      // Set up event listeners using correct SDK events
      session.on(SessionEvent.SESSION_STREAM_READY, (event) => {
        console.log("LiveAvatar stream ready:", event);
        setIsAvatarStreamAvailable(true);
        // setAvatarMediaStream(event.mediaStream);
        if (!startTimeStamp) startTimeStamp = Date.now();
      });

      session.on(SessionEvent.SESSION_STATE_CHANGED, (event) => {
        console.log("Session state changed:", event);
        // Handle different session states
      });

      session.on(SessionEvent.SESSION_DISCONNECTED, (reason) => {
        console.log("LiveAvatar session disconnected. Reason:", reason);
        if (reason === SessionDisconnectReason.SERVER_INITIATED) {
          // HeyGen ended the session server-side, as opposed to a network drop -
          // treat it the same as a normal end-of-interview so recording/cleanup
          // still runs instead of leaving the candidate stuck on this screen.
          console.log("LiveAvatar session was disconnected by the server");
          endInterviewAndRecord();
        }
      });

      session.on(AgentEventsEnum.SESSION_STATE_UPDATED, (event) => {
        console.log("AgentEvents : Session state updated:", event);
        // Handle different session states
      });

      session.on(AgentEventsEnum.SESSION_UPDATED, (event) => {
        console.log("AgentEvents : Session updated:", event);
        // Handle different session states
      });

      session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, (event) => {
        console.log("AgentEvents : Avatar Speak Started :", event);
        if (!startTimeStamp) startTimeStamp = Date.now();
        if (timeout) clearTimeout(timeout);
        console.log("AVATAR_START_TALKING : avatarMessage : ", avatarMessage);
        recordUserMessage();
        setDisplayBotVisualizer(true);
        setIsSynthesizing(true);
        setIsAvatarSpeaking(true);
        avatarEventsController.enqueue("avatar.speak_started");
        if (interrupt) {
          session.interrupt();
          setInterrupt(false);
          setClearCaption(true);
        }
        if (!AvatarStartedConversation) AvatarStartedConversation = true;
      });
      session.on(AgentEventsEnum.AVATAR_TRANSCRIPTION, (event) => {
        try {
          console.log("AgentEvents : Avatar Transcription :", event);
          if (event?.text) {
            avatarMessage += event.text;
            // Trim the accumulated message, not the incoming delta - getResponseWithoutBreakingWord
            // slices the *last* N chars of what it's given, so feeding it only the new token produced
            // a flickering fragment instead of a rolling caption (matches the USER_TRANSCRIPTION path
            // below, which already accumulates before trimming).
            setBotResponse(getResponseWithoutBreakingWord(avatarMessage));
            // modelCaptionsController?.enqueue(event.text);
          }
        } catch (e) {
          console.log(e);
        }
      });

      session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, (event) => {
        console.log("AgentEvents : Avatar Speak Ended :", event);
        setDisplayBotVisualizer(false);
        setIsSynthesizing(false);
        setIsAvatarSpeaking(false);
        avatarEventsController.enqueue("avatar.speak_ended");
        console.log("AVATAR_STOP_TALKING : avatarMessage : ", avatarMessage);

        if (
          avatarMessage.toLowerCase().includes("goodbye") ||
          avatarMessage.toLowerCase().includes("good bye") ||
          sessionEnded
        ) {
          console.log("AVATAR_STOP_TALKING : contains good bye");
          endInterviewAndRecord();
        } else {
          recordAvatarMessage();
        }
        if (!AvatarStartedConversation) AvatarStartedConversation = true;
      });

      // Added in liveavatar-web-sdk 0.0.18+. The `?? "session.stopped"` fallback
      // keeps this registration inert (not broken) on older SDK versions, where
      // AgentEventsEnum.SESSION_STOPPED is undefined and the event never fires.
      session.on(
        AgentEventsEnum.SESSION_STOPPED ?? "session.stopped",
        (event) => {
          console.log("AgentEvents : Session Stopped :", event);
          endInterviewAndRecord();
        },
      );

      session.on(AgentEventsEnum.USER_SPEAK_STARTED, (event) => {
        console.log("AgentEvents : User Speak Started :", event);
        // recordAvatarMessage();
        // Handle different session states
      });

      session.on(AgentEventsEnum.USER_SPEAK_ENDED, (event) => {
        console.log("AgentEvents : User Speak Ended :", event);
        // Handle different session states
      });

      session.on(AgentEventsEnum.USER_TRANSCRIPTION, (event) => {
        console.log("AgentEvents : User Transcription :", event);
        if (event?.text && AvatarStartedConversation) {
          userMessage += event.text;
          setUserResponse(getResponseWithoutBreakingWord(userMessage));
        }
        // Handle different session states
      });

      // Track events for avatar and user interactions
      // session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, (event) => {
      //   console.log("LiveAvatar event:", event);

      //   switch (event.type) {
      //     case "user_start":
      //       console.log("USER_START_TALKING : userMessage : ", userMessage);
      //       recordAvatarMessage();
      //       break;

      //     case "user_stop":
      //       console.log("User stopped speaking");
      //       break;

      //     case "avatar_start_talking":
      //       console.log("Avatar started talking", event);
      //       if (!startTimeStamp) startTimeStamp = Date.now();
      //       if (timeout) clearTimeout(timeout);
      //       console.log(
      //         "AVATAR_START_TALKING : avatarMessage : ",
      //         avatarMessage
      //       );
      //       recordUserMessage();
      //       setDisplayBotVisualizer(true);
      //       setIsSynthesizing(true);
      //       setIsAvatarSpeaking(true);
      //       avatarEventsController.enqueue("avatar.speak_started");
      //       if (interrupt) {
      //         session.interrupt();
      //         setInterrupt(false);
      //         setClearCaption(true);
      //       }
      //       if (!AvatarStartedConversation) AvatarStartedConversation = true;
      //       break;

      //     case "avatar_stop_talking":
      //       console.log("Avatar stopped talking", event);
      //       setDisplayBotVisualizer(false);
      //       setIsSynthesizing(false);
      //       setIsAvatarSpeaking(false);
      //       avatarEventsController.enqueue("avatar.speak_ended");
      //       console.log(
      //         "AVATAR_STOP_TALKING : avatarMessage : ",
      //         avatarMessage
      //       );

      //       if (
      //         avatarMessage.toLowerCase().includes("goodbye") ||
      //         avatarMessage.toLowerCase().includes("good bye") ||
      //         sessionEnded
      //       ) {
      //         if (timeout) clearTimeout(timeout);
      //         console.log("AVATAR_STOP_TALKING : contains good bye");
      //         if (!sessionEnded) {
      //           sessionEnded = true;
      //           setCanEndServerSessionForLiveAvatar(false);
      //           Promise.all([
      //             recordUserMessage(),
      //             recordAvatarMessage(),
      //           ]).finally(() => {
      //             setCanEndServerSessionForLiveAvatar(true);
      //           });
      //         }
      //         timeout = setTimeout(() => {
      //           setInterviewEnded(true);
      //         }, 1000);
      //       }
      //       break;

      //     case "avatar_talking_message":
      //       try {
      //         console.log("Avatar talking message:", event.message);
      //         avatarMessage += event.message;
      //         modelCaptionsController?.enqueue(event.message);
      //       } catch (e) {
      //         console.log("Error handling avatar talking message:", e);
      //       }
      //       break;

      //     case "user_talking_message":
      //       console.log("User talking message:", event.message);
      //       if (AvatarStartedConversation) {
      //         userMessage += event.message;
      //         setUserResponse(getResponseWithoutBreakingWord(userMessage));
      //       }
      //       break;
      //   }
      // });

      console.log("Video Avatar Id : ", videoAvatarId);
      console.log("Video Voice Id : ", videoVoiceId);

      // Start the session with configuration (matching HeyGen feature parity)
      const res = await session.start();
      console.log("response from liveavatar : ", res);

      // Send initial greeting
      setTimeout(() => {
        session.message("hi");
      }, 500);
    } catch (error) {
      console.error("Error starting LiveAvatar session:", error);
      Sentry.captureException(error);
      setSessionError(
        error?.message || "Failed to start the LiveAvatar session",
      );
    }
  }

  return null;
}
